import { SnelStartSettings } from '../../components/library/snelstart-settings/SnelStartSettings';
import { SnelStartSalesOrderTemplate } from '../../api/company-settings';
import './tenant-details.scss';
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import ScrollView from 'devextreme-react/scroll-view';
import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import DateBox from 'devextreme-react/date-box';
import SelectBox from 'devextreme-react/select-box';
import DataGrid, { Column, Scrolling } from 'devextreme-react/data-grid';
import { ProfileCard } from '../../components/library/profile-card/ProfileCard';
import { withLoadPanel } from '../../utils/withLoadPanel';
import { useAuth } from '../../contexts/auth';
import {
  TenantDto,
  TenantUserDto,
  AccountingProviderDto,
  getAccountingProvider,
  getSnelStartSalesOrderTemplates,
  configureAccountingProvider,
  removeAccountingProvider,
  getOAuthInitiateUrl,
  deactivateTenant,
  reactivateTenant,
  deleteTenant,
  createTenantUser,
  listTenants,
  listTenantUsers,
  updateTenantSettings,
  syncTenantEmployees,
  resendTenantActivation,
} from '../../api/tenants';

export interface State {
  tenantOid: string;
}

const providerTypes = ['None', 'ExactOnline', 'SnelStart'];

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

// ── Content Component ────────────────────────────────────

type ContentProps = {
  tenantData: TenantDto | null;
  tenantUsers: TenantUserDto[];
  accountingProvider: AccountingProviderDto | null;
  snelStartTemplates: SnelStartSalesOrderTemplate[];
  providerFormData: any;
  setProviderFormData: (fn: any) => void;
  userFormData: any;
  setUserFormData: (fn: any) => void;
  settingsFormData: any;
  setSettingsFormData: (fn: any) => void;
  handleContentScrolled: (reachedTop: boolean) => void;
  onSaveProvider: () => void;
  onRemoveProvider: () => void;
  onOAuthClick: () => void;
  onCreateUser: () => void;
  onSaveSettings: () => void;
  onSyncEmployees: () => void;
  savingProvider: boolean;
  creatingUser: boolean;
  savingSettings: boolean;
};

const TenantDetailsContent = ({
  tenantData,
  tenantUsers,
  accountingProvider,
  snelStartTemplates,
  providerFormData,
  setProviderFormData,
  userFormData,
  setUserFormData,
  settingsFormData,
  setSettingsFormData,
  handleContentScrolled,
  onSaveProvider,
  onRemoveProvider,
  onOAuthClick,
  onCreateUser,
  onSaveSettings,
  onSyncEmployees,
  savingProvider,
  creatingUser,
  savingSettings,
}: ContentProps) => {
  const onScroll = useCallback(
    (reachedTop) => {
      handleContentScrolled(reachedTop);
    },
    [handleContentScrolled]
  );

  const trialDays = tenantData?.TrialDaysRemaining ?? 0;
  const isTrialActive = tenantData?.IsTrialActive ?? false;

  return (
    <ScrollView className='view-wrapper-scroll' onScroll={onScroll}>
      <div className='cards-container'>

        {/* ── Tenant Info ────────────────────── */}
        <ProfileCard
          wrapperCssClass='profile-card basic-info-card'
          title='Tenant Informatie'
          colCount={2}
          cardData={(tenantData || {}) as Record<string, string>}
          items={[]}
          onDataChanged={noop}
        >
          <div className='basic-info-top-item profile-card-top-item'>
            <div className='image-wrapper'>
              <i className='dx-icon dx-icon-box' />
            </div>
            <div style={{ marginLeft: '10px' }}>
              <div className='title-text'>{tenantData?.CompanyName}</div>
              <div className='subtitle-text'>Domein: {tenantData?.Name}</div>
              <div className='subtitle-text'>
                Status: {tenantData?.IsActive ? '✓ Actief' : '✗ Inactief'}
              </div>
            </div>
          </div>

          <div className='info-grid'>
            <div className='info-item'>
              <span className='info-label'>Contact Email</span>
              <span className='info-value'>{tenantData?.ContactEmail || '—'}</span>
            </div>
            <div className='info-item'>
              <span className='info-label'>Aangemaakt</span>
              <span className='info-value'>
                {tenantData?.CreatedDate
                  ? new Date(tenantData.CreatedDate).toLocaleDateString('nl-NL')
                  : '—'}
              </span>
            </div>
            <div className='info-item'>
              <span className='info-label'>Gebruikers</span>
              <span className='info-value'>
                {tenantUsers.length}{tenantData?.MaxUsers ? ` / ${tenantData.MaxUsers}` : ' (onbeperkt)'}
              </span>
            </div>
            <div className='info-item'>
              <span className='info-label'>Proefperiode</span>
              <span className={`info-value ${isTrialActive ? 'trial-active' : 'trial-expired'}`}>
                {isTrialActive
                  ? `${trialDays} dagen resterend`
                  : tenantData?.TrialEndDate ? 'Verlopen' : 'Geen proefperiode'}
              </span>
            </div>
          </div>
        </ProfileCard>

        {/* ── Trial & Limits Settings ────────── */}
        <ProfileCard
          wrapperCssClass='profile-card settings-card'
          title='Instellingen'
          colCount={1}
          cardData={{} as Record<string, string>}
          items={[]}
          onDataChanged={noop}
        >
          <div className='profile-card-top-item'>
            <div className='image-wrapper'>
              <i className='dx-icon dx-icon-preferences' />
            </div>
            <div>
              <div className='title-text'>Limieten & Proefperiode</div>
              <div className='subtitle-text'>Beheer gebruikerslimieten en proeftijd</div>
            </div>
          </div>

          <div className='settings-form'>
            <NumberBox
              label='Max. Gebruikers (0 = onbeperkt)'
              labelMode='floating'
              min={0}
              max={1000}
              showSpinButtons
              value={settingsFormData.maxUsers}
              onValueChanged={(e) =>
                setSettingsFormData((prev) => ({ ...prev, maxUsers: e.value }))
              }
            />
            <DateBox
              label='Proefperiode Einddatum'
              labelMode='floating'
              type='date'
              displayFormat='dd-MM-yyyy'
              value={settingsFormData.trialEndDate}
              onValueChanged={(e) =>
                setSettingsFormData((prev) => ({ ...prev, trialEndDate: e.value }))
              }
            />
            <TextBox
              label='Contact Email'
              labelMode='floating'
              value={settingsFormData.contactEmail}
              onValueChanged={(e) =>
                setSettingsFormData((prev) => ({ ...prev, contactEmail: e.value }))
              }
            />
            <TextBox
              label='Notities'
              labelMode='floating'
              value={settingsFormData.notes}
              onValueChanged={(e) =>
                setSettingsFormData((prev) => ({ ...prev, notes: e.value }))
              }
            />
            <div className='action-buttons'>
              <Button
                text='Opslaan'
                icon='save'
                type='default'
                stylingMode='contained'
                disabled={savingSettings}
                onClick={onSaveSettings}
              />
            </div>
          </div>
        </ProfileCard>

        {/* ── Users Overview ─────────────────── */}
        <ProfileCard
          wrapperCssClass='profile-card users-card full-width'
          title={`Gebruikers (${tenantUsers.length}${tenantData?.MaxUsers ? ` / ${tenantData.MaxUsers}` : ''})`}
          colCount={1}
          cardData={{} as Record<string, string>}
          items={[]}
          onDataChanged={noop}
        >
          <div className='profile-card-top-item'>
            <div className='image-wrapper'>
              <i className='dx-icon dx-icon-group' />
            </div>
            <div>
              <div className='title-text'>{tenantUsers.length} gebruikers</div>
              <div className='subtitle-text'>
                {tenantData?.MaxUsers
                  ? `Limiet: ${tenantData.MaxUsers} gebruikers`
                  : 'Geen limiet ingesteld'}
              </div>
            </div>
          </div>

          <DataGrid
            dataSource={tenantUsers}
            keyExpr='Oid'
            showBorders={false}
            showRowLines
            columnAutoWidth
            noDataText='Geen gebruikers gevonden'
            className='users-grid'
          >
            <Scrolling mode='virtual' />
            <Column dataField='UserName' caption='Email' />
            <Column dataField='FirstName' caption='Voornaam' />
            <Column dataField='LastName' caption='Achternaam' />
            <Column
              dataField='IsActive'
              caption='Status'
              width={100}
              cellRender={(data) => (
                <span className={data.value ? 'status-active' : 'status-inactive'}>
                  {data.value ? '✓ Actief' : '✗ Inactief'}
                </span>
              )}
            />
          </DataGrid>

          <div className='action-buttons' style={{ marginTop: '12px' }}>
            <Button
              text='Sync Werknemers'
              icon='refresh'
              type='normal'
              stylingMode='outlined'
              hint='Maak werknemers aan voor gebruikers die er nog geen hebben'
              onClick={onSyncEmployees}
            />
          </div>
        </ProfileCard>

        {/* ── Create User ────────────────────── */}
        <ProfileCard
          wrapperCssClass='profile-card user-creation-card'
          title='Gebruiker Aanmaken'
          colCount={1}
          cardData={{} as Record<string, string>}
          items={[]}
          onDataChanged={noop}
        >
          <div className='profile-card-top-item'>
            <div className='image-wrapper'>
              <i className='dx-icon dx-icon-user' />
            </div>
            <div>
              <div className='title-text'>Nieuwe gebruiker</div>
              <div className='subtitle-text'>
                {tenantData?.MaxUsers
                  ? `${tenantUsers.length} / ${tenantData.MaxUsers} plekken bezet`
                  : 'Maak een nieuwe gebruiker voor deze tenant'}
              </div>
            </div>
          </div>

          <div className='user-form'>
            <TextBox
              label='Email'
              labelMode='floating'
              value={userFormData.email}
              onValueChanged={(e) =>
                setUserFormData((prev) => ({ ...prev, email: e.value }))
              }
            />
            <TextBox
              label='Wachtwoord'
              labelMode='floating'
              mode='password'
              value={userFormData.password}
              onValueChanged={(e) =>
                setUserFormData((prev) => ({ ...prev, password: e.value }))
              }
            />
            <TextBox
              label='Voornaam'
              labelMode='floating'
              value={userFormData.firstName}
              onValueChanged={(e) =>
                setUserFormData((prev) => ({ ...prev, firstName: e.value }))
              }
            />
            <TextBox
              label='Achternaam'
              labelMode='floating'
              value={userFormData.lastName}
              onValueChanged={(e) =>
                setUserFormData((prev) => ({ ...prev, lastName: e.value }))
              }
            />
            <div className='action-buttons'>
              <Button
                text='Gebruiker Aanmaken'
                icon='plus'
                type='success'
                stylingMode='contained'
                disabled={creatingUser}
                onClick={onCreateUser}
              />
            </div>
          </div>
        </ProfileCard>

        {/* ── Accounting Provider ────────────── */}
        <ProfileCard
          wrapperCssClass='profile-card accounting-card'
          title='Boekhoudkoppeling'
          colCount={1}
          cardData={(accountingProvider || {}) as Record<string, string>}
          items={[]}
          onDataChanged={noop}
        >
          <div className='profile-card-top-item'>
            <div className='image-wrapper'>
              <i className='dx-icon dx-icon-chart' />
            </div>
            <div>
              <div className='title-text'>
                {accountingProvider?.ProviderType === 'ExactOnline'
                  ? 'Exact Online'
                  : accountingProvider?.ProviderType === 'SnelStart' ? 'SnelStart' : 'Geen koppeling'}
              </div>
              {((accountingProvider?.ProviderType === 'ExactOnline' && accountingProvider.HasRefreshToken) || accountingProvider?.HasSnelStartConnection) && (
                <div className='subtitle-text'>✓ Geautoriseerd</div>
              )}
            </div>
          </div>

          <div className='provider-form'>
            <SelectBox
              label='Provider Type'
              labelMode='floating'
              items={providerTypes}
              value={providerFormData.providerType}
              onValueChanged={(e) =>
                setProviderFormData((prev) => ({ ...prev, providerType: e.value }))
              }
            />

            {providerFormData.providerType === 'SnelStart' && (
              <SnelStartSettings
                subscriptionKey={providerFormData.snelStartSubscriptionKey} clientKey={providerFormData.snelStartClientKey}
                templateId={providerFormData.snelStartSalesOrderTemplateId} templates={snelStartTemplates}
                hasSubscriptionKey={accountingProvider?.HasSnelStartSubscriptionKey} hasClientKey={accountingProvider?.HasSnelStartClientKey}
                onSubscriptionKeyChanged={(value) => setProviderFormData((prev) => ({ ...prev, snelStartSubscriptionKey: value }))}
                onClientKeyChanged={(value) => setProviderFormData((prev) => ({ ...prev, snelStartClientKey: value }))}
                onTemplateChanged={(value) => setProviderFormData((prev) => ({ ...prev, snelStartSalesOrderTemplateId: value }))} />
            )}

            {providerFormData.providerType === 'ExactOnline' && (
              <>
                <TextBox
                  label='Client ID'
                  labelMode='floating'
                  value={providerFormData.exactOnlineClientId}
                  onValueChanged={(e) =>
                    setProviderFormData((prev) => ({
                      ...prev,
                      exactOnlineClientId: e.value,
                    }))
                  }
                />
                <TextBox
                  label='Client Secret'
                  labelMode='floating'
                  mode='password'
                  value={providerFormData.exactOnlineClientSecret}
                  onValueChanged={(e) =>
                    setProviderFormData((prev) => ({
                      ...prev,
                      exactOnlineClientSecret: e.value,
                    }))
                  }
                />
                <TextBox
                  label='Callback URL (optioneel)'
                  labelMode='floating'
                  value={providerFormData.exactOnlineCallbackUrl}
                  onValueChanged={(e) =>
                    setProviderFormData((prev) => ({
                      ...prev,
                      exactOnlineCallbackUrl: e.value,
                    }))
                  }
                />
              </>
            )}

            <div className='action-buttons'>
              <Button
                text='Opslaan'
                icon='save'
                type='default'
                stylingMode='contained'
                disabled={savingProvider}
                onClick={onSaveProvider}
              />
              {accountingProvider && accountingProvider.ProviderType !== 'None' && (
                <>
                  {accountingProvider.ProviderType === 'ExactOnline' && <Button
                    text='OAuth Autoriseren'
                    icon='key'
                    type='success'
                    stylingMode='outlined'
                    onClick={onOAuthClick}
                  />}
                  <Button
                    text='Verwijderen'
                    icon='trash'
                    type='danger'
                    stylingMode='text'
                    onClick={onRemoveProvider}
                  />
                </>
              )}
            </div>
          </div>
        </ProfileCard>

      </div>
    </ScrollView>
  );
};

const TenantDetailsContentWithLoadPanel = withLoadPanel(TenantDetailsContent);

// ── Main Page Component ──────────────────────────────────

export const TenantDetails = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const state = useLocation().state as State;

  const [tenantData, setTenantData] = useState<TenantDto | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUserDto[]>([]);
  const [accountingProvider, setAccountingProvider] = useState<AccountingProviderDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isContentScrolled, setIsContentScrolled] = useState(false);

  const [snelStartTemplates, setSnelStartTemplates] = useState<SnelStartSalesOrderTemplate[]>([]);

  const [providerFormData, setProviderFormData] = useState({
    providerType: 'None',
    exactOnlineClientId: '',
    exactOnlineClientSecret: '',
    exactOnlineCallbackUrl: '',
    snelStartSubscriptionKey: '',
    snelStartClientKey: '',
    snelStartSalesOrderTemplateId: null as string | null,
  });

  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });

  const [settingsFormData, setSettingsFormData] = useState({
    maxUsers: 0,
    trialEndDate: null as string | null,
    contactEmail: '',
    notes: '',
  });

  const [savingProvider, setSavingProvider] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const handleContentScrolled = useCallback((reachedTop) => {
    setIsContentScrolled(!reachedTop);
  }, []);

  // ── Data loading ───────────────────────────────────────

  const loadTenantData = useCallback(async() => {
    if (!state?.tenantOid) return;
    setIsLoading(true);
    try {
      const tenants = await listTenants(user?.token);
      const tenant = tenants.find((t) => t.Oid === state.tenantOid);
      if (tenant) {
        setTenantData(tenant);
        setUserFormData((prev) => ({ ...prev, email: `@${tenant.Name}` }));
        setSettingsFormData({
          maxUsers: tenant.MaxUsers,
          trialEndDate: tenant.TrialEndDate,
          contactEmail: tenant.ContactEmail || '',
          notes: '',
        });
      }
    } catch (error: any) {
      notify(error?.response?.data || 'Fout bij laden tenant', 'error', 3000);
    } finally {
      setIsLoading(false);
    }
  }, [state?.tenantOid, user?.token]);

  const loadTenantUsers = useCallback(async() => {
    if (!state?.tenantOid) return;
    try {
      const users = await listTenantUsers(state.tenantOid, user?.token);
      setTenantUsers(users);
    } catch {
      setTenantUsers([]);
    }
  }, [state?.tenantOid, user?.token]);

  const loadAccountingProvider = useCallback(async() => {
    if (!state?.tenantOid) return;
    try {
      const data = await getAccountingProvider(state.tenantOid, user?.token);
      setAccountingProvider(data);
      setSnelStartTemplates([]);
      if (data.ProviderType === 'SnelStart' && data.HasSnelStartConnection) {
        try { setSnelStartTemplates(await getSnelStartSalesOrderTemplates(state.tenantOid, user?.token)); }
        catch { notify('SnelStart sjablonen konden niet worden geladen.', 'warning', 3000); }
      }
      setProviderFormData({
        providerType: data.ProviderType || 'None',
        exactOnlineClientId: data.ExactOnlineClientId || '',
        exactOnlineClientSecret: '',
        exactOnlineCallbackUrl: data.ExactOnlineCallbackUrl || '',
        snelStartSubscriptionKey: '',
        snelStartClientKey: '',
        snelStartSalesOrderTemplateId: data.SnelStartSalesOrderTemplateId || null,
      });
    } catch {
      setAccountingProvider(null);
      setProviderFormData({
        providerType: 'None',
        exactOnlineClientId: '',
        exactOnlineClientSecret: '',
        exactOnlineCallbackUrl: '',
        snelStartSubscriptionKey: '',
        snelStartClientKey: '',
        snelStartSalesOrderTemplateId: null as string | null,
      });
    }
  }, [state?.tenantOid, user?.token]);

  useEffect(() => {
    loadTenantData();
    loadTenantUsers();
    loadAccountingProvider();
  }, [loadTenantData, loadTenantUsers, loadAccountingProvider]);

  const onBack = useCallback(() => {
    navigate('/tenant-admin');
  }, [navigate]);

  // ── Save settings ──────────────────────────────────────

  const onSaveSettings = useCallback(async() => {
    if (!state?.tenantOid) return;
    setSavingSettings(true);
    try {
      await updateTenantSettings(state.tenantOid, {
        maxUsers: settingsFormData.maxUsers,
        trialEndDate: settingsFormData.trialEndDate || undefined,
        contactEmail: settingsFormData.contactEmail,
        notes: settingsFormData.notes,
      }, user?.token);
      notify('Instellingen opgeslagen', 'success', 2000);
      await loadTenantData();
    } catch (error: any) {
      const msg = error?.response?.data || 'Fout bij opslaan instellingen';
      notify(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error', 3000);
    } finally {
      setSavingSettings(false);
    }
  }, [state?.tenantOid, settingsFormData, user?.token, loadTenantData]);

  // ── Sync employees ─────────────────────────────────────

  const onSyncEmployees = useCallback(async() => {
    if (!state?.tenantOid) return;
    try {
      const result = await syncTenantEmployees(state.tenantOid, user?.token);
      notify(result?.Message || 'Werknemers gesynchroniseerd', 'success', 3000);
      await loadTenantUsers();
    } catch (error: any) {
      const msg = error?.response?.data || 'Fout bij synchroniseren werknemers';
      notify(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error', 3000);
    }
  }, [state?.tenantOid, user?.token, loadTenantUsers]);

  // ── Provider actions ───────────────────────────────────

  const onSaveProvider = useCallback(async() => {
    if (!state?.tenantOid) return;
    setSavingProvider(true);
    try {
      await configureAccountingProvider(state.tenantOid, providerFormData, user?.token);
      notify('Boekhoudprovider opgeslagen', 'success', 2000);
      await loadAccountingProvider();
    } catch (error: any) {
      const msg = error?.response?.data || 'Fout bij opslaan provider';
      notify(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error', 3000);
    } finally {
      setSavingProvider(false);
    }
  }, [state?.tenantOid, providerFormData, user?.token, loadAccountingProvider]);

  const onRemoveProvider = useCallback(async() => {
    if (!state?.tenantOid) return;
    const result = await confirm('Weet je zeker dat je de provider wilt verwijderen?', 'Provider verwijderen');
    if (!result) return;
    try {
      await removeAccountingProvider(state.tenantOid, user?.token);
      notify('Provider verwijderd', 'success', 2000);
      await loadAccountingProvider();
    } catch (error: any) {
      notify(error?.response?.data || 'Fout bij verwijderen', 'error', 3000);
    }
  }, [state?.tenantOid, user?.token, loadAccountingProvider]);

  const onOAuthClick = useCallback(() => {
    if (!state?.tenantOid) return;
    const url = getOAuthInitiateUrl(state.tenantOid);
    window.open(url, '_blank');
    notify('OAuth venster geopend. Autoriseer de app.', 'info', 3000);
  }, [state?.tenantOid]);

  // ── Create user ────────────────────────────────────────

  const onCreateUser = useCallback(async() => {
    if (!state?.tenantOid) return;
    if (!userFormData.email || !userFormData.password) {
      notify('Email en wachtwoord zijn verplicht', 'warning', 2000);
      return;
    }
    setCreatingUser(true);
    try {
      await createTenantUser(state.tenantOid, userFormData, user?.token);
      notify('Gebruiker aangemaakt', 'success', 2000);
      setUserFormData({
        email: `@${tenantData?.Name || ''}`,
        password: '',
        firstName: '',
        lastName: '',
      });
      await loadTenantUsers();
    } catch (error: any) {
      const msg = error?.response?.data || 'Fout bij aanmaken gebruiker';
      notify(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error', 3000);
    } finally {
      setCreatingUser(false);
    }
  }, [state?.tenantOid, userFormData, user?.token, tenantData?.Name, loadTenantUsers]);

  // ── Tenant lifecycle actions ───────────────────────────

  const onDeactivate = useCallback(async() => {
    if (!state?.tenantOid || !tenantData) return;
    const result = await confirm(
      `Weet je zeker dat je '${tenantData.CompanyName}' wilt deactiveren?`,
      'Tenant deactiveren'
    );
    if (!result) return;
    try {
      await deactivateTenant(state.tenantOid, user?.token);
      notify('Tenant gedeactiveerd', 'success', 2000);
      await loadTenantData();
    } catch (error: any) {
      notify(error?.response?.data || 'Fout bij deactiveren', 'error', 3000);
    }
  }, [state?.tenantOid, tenantData, user?.token, loadTenantData]);

  const onReactivate = useCallback(async() => {
    if (!state?.tenantOid) return;
    try {
      await reactivateTenant(state.tenantOid, user?.token);
      notify('Tenant geheractiveerd', 'success', 2000);
      await loadTenantData();
    } catch (error: any) {
      notify(error?.response?.data || 'Fout bij heractiveren', 'error', 3000);
    }
  }, [state?.tenantOid, user?.token, loadTenantData]);

  const onDelete = useCallback(async() => {
    if (!state?.tenantOid || !tenantData) return;
    const result = await confirm(
      `⚠️ WAARSCHUWING: Dit verwijdert tenant '${tenantData.CompanyName}' permanent inclusief de database!\n\nWeet je het zeker?`,
      'Tenant verwijderen'
    );
    if (!result) return;
    try {
      await deleteTenant(state.tenantOid, true, user?.token);
      notify('Tenant verwijderd', 'success', 2000);
      navigate('/tenant-admin');
    } catch (error: any) {
      notify(error?.response?.data || 'Fout bij verwijderen', 'error', 3000);
    }
  }, [state?.tenantOid, tenantData, user?.token, navigate]);

  const onResendActivation = useCallback(async() => {
    if (!tenantData?.ContactEmail) return;
    try {
      await resendTenantActivation(tenantData.ContactEmail, user?.token);
      notify('Activatie e-mail opnieuw verzonden', 'success', 3000);
    } catch (error: any) {
      notify(error?.response?.data?.message || error?.response?.data || 'Kon activatie e-mail niet verzenden', 'error', 3000);
    }
  }, [tenantData, user?.token]);

  // ── Render ─────────────────────────────────────────────

  return (
    <div className='view-host tenant-details'>
      <div className='view-wrapper'>
        <Toolbar className={`theme-dependent ${isContentScrolled ? 'scrolled' : ''}`}>
          <Item location='before'>
            <Button icon='back' stylingMode='text' onClick={onBack} />
          </Item>
          <Item location='before'>
            <div className='header-text'>{tenantData?.CompanyName || 'Tenant Details'}</div>
          </Item>
          <Item location='after' locateInMenu='auto'>
            {tenantData?.IsActive ? (
              <Button
                text='Deactiveren'
                icon='clear'
                type='danger'
                stylingMode='outlined'
                onClick={onDeactivate}
              />
            ) : (
              <Button
                text='Heractiveren'
                icon='check'
                type='success'
                stylingMode='outlined'
                onClick={onReactivate}
              />
            )}
          </Item>
          <Item location='after' locateInMenu='auto'>
            <Button
              text='Verwijderen'
              icon='trash'
              type='danger'
              stylingMode='text'
              onClick={onDelete}
            />
          </Item>
          {!tenantData?.IsActivated && tenantData?.ContactEmail && (
            <Item location='after' locateInMenu='auto'>
              <Button
                text='Activatielink versturen'
                icon='email'
                type='default'
                stylingMode='outlined'
                onClick={onResendActivation}
              />
            </Item>
          )}
        </Toolbar>
        <TenantDetailsContentWithLoadPanel
          tenantData={tenantData}
          tenantUsers={tenantUsers}
          accountingProvider={accountingProvider}
          snelStartTemplates={snelStartTemplates}
          providerFormData={providerFormData}
          setProviderFormData={setProviderFormData}
          userFormData={userFormData}
          setUserFormData={setUserFormData}
          settingsFormData={settingsFormData}
          setSettingsFormData={setSettingsFormData}
          handleContentScrolled={handleContentScrolled}
          onSaveProvider={onSaveProvider}
          onRemoveProvider={onRemoveProvider}
          onOAuthClick={onOAuthClick}
          onCreateUser={onCreateUser}
          onSaveSettings={onSaveSettings}
          onSyncEmployees={onSyncEmployees}
          savingProvider={savingProvider}
          creatingUser={creatingUser}
          savingSettings={savingSettings}
          hasData={!isLoading}
          loading={isLoading}
          panelProps={{
            container: '.view-wrapper',
            position: { of: '.content' },
          }}
        />
      </div>
    </div>
  );
};
